// Cross-origin "Script error." swallower + dev overlay killer.
//
// The browser reports any uncaught exception coming from a cross-origin script
// (YouTube IFrame API, Web Speech API, browser extensions, the Emergent
// preview badge, etc.) as the bare string "Script error." with no filename
// and no stack. CRA's react-error-overlay catches these via window.onerror
// and renders a giant red "Uncaught runtime errors" panel that blocks the
// entire UI — so the user can't click Read, Profile, or Music until they
// dismiss it (and it instantly comes back the next time an unrelated
// cross-origin script throws).
//
// Two layers of defense (both safe in production where react-error-overlay
// isn't shipped at all):
//   1. Tell react-error-overlay to stop reporting runtime errors entirely.
//      This is the supported API (`stopReportingRuntimeErrors`) and it nukes
//      the red panel without affecting real errors, which still surface in
//      the console and React error boundaries.
//   2. Belt-and-suspenders: a capture-phase `error` listener that swallows
//      bare "Script error." events from cross-origin scripts before any
//      other listener (including any future overlay) sees them.

function isCrossOriginNoise(event) {
  if (!event) return false;
  const msg = (event.message || "").trim();
  // Browsers normalise this to "Script error." or just "Script error".
  if (msg === "Script error." || msg === "Script error") return true;
  // Some browsers leave message empty but also strip filename. If we have
  // neither a message nor a same-origin filename, treat it as noise too.
  if (!msg && !event.filename) return true;
  return false;
}

export function installScriptErrorSilencer() {
  if (typeof window === "undefined") return;
  if (window.__hwScriptErrorSilencerInstalled) return;
  window.__hwScriptErrorSilencerInstalled = true;

  // Layer 1: turn off CRA's runtime error overlay completely in dev.
  // In production builds, this import gets tree-shaken / no-ops because
  // process.env.NODE_ENV === "production" and we wrap the call.
  if (process.env.NODE_ENV !== "production") {
    // Dynamic import so a missing module never breaks the bundle.
    import("react-error-overlay")
      .then((overlay) => {
        try {
          overlay.stopReportingRuntimeErrors && overlay.stopReportingRuntimeErrors();
          overlay.dismissRuntimeErrors && overlay.dismissRuntimeErrors();
        } catch (_) { /* noop */ }
      })
      .catch(() => { /* react-error-overlay not installed — fine */ });
  }

  // Layer 2: capture phase so we run before react-error-overlay's listener.
  window.addEventListener("error", (event) => {
    if (isCrossOriginNoise(event)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }, true);

  // Unhandled-promise rejections from cross-origin scripts arrive with
  // event.reason === undefined or a plain string — also swallow those so they
  // don't trip the overlay.
  window.addEventListener("unhandledrejection", (event) => {
    const r = event.reason;
    if (r == null) {
      event.preventDefault();
      return;
    }
    if (typeof r === "string" && /script error/i.test(r)) {
      event.preventDefault();
    }
  }, true);
}
